import {ChangeDetectorRef, Component, ElementRef, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TableAction, TableColumn, TableColumnType, TableComponent} from "../../../components/table/table.component";
import {Equipment} from "../../../models/equipment";
import {EquipmentManagerService} from "../../../services/equipment-manager.service";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthoringManagerService} from "../../../services/authoring-manager.service";
import {PhysicalAssetManagerService} from "../../../services/physical-asset-manager.service";
import {PhysicalAsset} from "../../../models/physical-asset";
import {FieldServiceManagerService} from "../../../services/field-service-manager.service";
import {FieldService} from "../../../models/field-service";
import {UiManagerService} from "../../../services/ui-manager.service";
import {RepositoryService} from "../../../services/repository.service";
import {ApiStatus} from "../../../models/api-response";
import {Paginator} from "../../../models/paginator";
import {Procedure, ProcedureType} from "../../../models/procedure";
import {Task} from "../../../models/task";
import {Resource, ResourceType, ResourceUtils} from "../../../models/resource";
import {ResourceManager} from "../../../services/resource-manager";
import {DetailCardContent} from "../../../components/detail-card/detail-card.component";
import {environment} from "@environments/environment";
import {TranslatePipe} from '@ngx-translate/core';
import {InputDialogData} from "../../../components/dialogs/input-dialog/input-dialog.component";

enum CreationMode {
  NONE, NEW, UPDATE
}

@Component({
  selector: 'app-create-equipment-page',
  templateUrl: './create-equipment-page.component.html',
  styleUrls: ['./create-equipment-page.component.scss']
})
export class CreateEquipmentPageComponent implements OnInit {

  @ViewChild(TableComponent)
  private assetTable: TableComponent;

  isLoading = true;

  mode: CreationMode = CreationMode.NONE;
  equipment: Equipment;
  equipmentImage: string;

  resources: Resource[];
  physicalAssets: PhysicalAsset[];
  fieldServices: FieldService[];

  paginatorProcedure: Paginator<Procedure>;
  paginatorPhysicalAssets: Paginator<PhysicalAsset>;
  paginatorFieldService: Paginator<FieldService>;
  paginatorResources: Paginator<Resource>;

  constructor(
    private equipmentManagerService: EquipmentManagerService,
    private physicalAssetManagerService: PhysicalAssetManagerService,
    private fieldServiceManagerService: FieldServiceManagerService,
    public authoringManagerService: AuthoringManagerService,
    private uiManagerService: UiManagerService,
    private repositoryService: RepositoryService,
    private resourceManager: ResourceManager,
    private cdr: ChangeDetectorRef,
    public route: ActivatedRoute,
    public router: Router,
    private translatePipe: TranslatePipe
  ) { }

  ngOnInit(): void {

    // Initialize new equipment
    this.equipment = new Equipment();

    this.route.paramMap.subscribe(async params => {

      const equipmentId = params.get('id');

      if(equipmentId) {

        this.paginatorProcedure = new Paginator<Procedure>();
        this.paginatorPhysicalAssets = new Paginator<PhysicalAsset>();
        this.paginatorFieldService = new Paginator<FieldService>({ignorePagination: true});
        this.paginatorResources = new Paginator<Resource>({pageSize: 16});

        // Fetch equipment
        this.equipment = await this.equipmentManagerService.getEquipment(equipmentId);

        // Update callbacks
        this.paginatorProcedure.onPageResults = async (procedures: Procedure[]) => {

          // For each procedure get all tasks
          await Promise.all(procedures.map(async procedure => {
            procedure.tasksTotal = (await this.authoringManagerService.getTasks(procedure.id, new Paginator<Task>({ignorePagination: true}))).length;
            return procedure;
          }));

          this.equipment.procedures = procedures;
        };

        this.paginatorPhysicalAssets.onPageResults = (physicalAssets: PhysicalAsset[]) => {
          this.physicalAssets = physicalAssets;
          this.assetTable?.refresh();
        };

        this.paginatorFieldService.onPageResults = (fieldServices: FieldService[]) => {
          this.fieldServices = fieldServices;
          this.assetTable?.refresh();
        };

        this.paginatorResources.onPageResults = (resources: Resource[]) => {
          this.resources = resources;
        };

        await this.authoringManagerService.getProcedures(equipmentId, this.paginatorProcedure);
        await this.physicalAssetManagerService.getPhysicalAssets(equipmentId, null, this.paginatorPhysicalAssets);
        await this.fieldServiceManagerService.getFieldServices(null, this.paginatorFieldService);
        await this.resourceManager.getResources(this.equipment.id, this.paginatorResources);

        if (this.equipment.imageUrl) {
          this.equipmentImage = this.equipment.imageUrl;
        }

        this.mode = CreationMode.UPDATE;

      } else {

        this.mode = CreationMode.NEW;

      }

      this.isLoading = false;

    });

  }

  getCardTitle() {
    switch (this.mode) {
      case CreationMode.NONE:
        return '&nbsp;';
      case CreationMode.NEW:
        return this.translatePipe.transform('Create equipment');
      case CreationMode.UPDATE:
        return `${this.translatePipe.transform('Manage equipment')} ${this.equipment.name}`;
    }
  }

  getPhysicalAssetsTableColumns(): TableColumn[] {
    return [
      { key: 'serial_number', isMasterKey: true, type: TableColumnType.DEFAULT, title: 'Serial number' },
      { key: 'field_service', type: TableColumnType.DEFAULT, title: 'Field service' },
      { key: 'deployed_at', type: TableColumnType.DATE, title: 'Setup date' },
      { key: 'last_maintenance_at', type: TableColumnType.DATE, title: 'Last maintenance' },
      { key: 'last_maintenance_reason', type: TableColumnType.DEFAULT, title: 'Last maintenance description' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getPhysicalAssetsTableData() {
    return this.physicalAssets?.map(asset => {
      return {
        id: asset.id,
        serial_number: asset.serialNumber,
        field_service: this.fieldServices.find(x => asset.fieldService && x.id == asset.fieldService.id)?.name || 'No field service',
        deployed_at: new Date(asset.deployedAt),
        last_maintenance_at: asset.lastMaintenance ? new Date(asset.lastMaintenance.scheduledAt) : '-',
        last_maintenance_reason: asset.lastMaintenance ? asset.lastMaintenance.reason : '-',
        actions: [
          {
            icon: 'qr_code', callback: () => {
              this.uiManagerService.showQRCode({
                assetId: asset.id.toString(),
                equipmentName: asset.model.name,
                serialNumber: asset.serialNumber
              });
            }
          },
          {
            icon: 'delete', color: 'warn', callback: () => {
              this.uiManagerService.showConfirmDialog('Remove asset', 'Are you sure do you want to remove this asset?<br>This operation is irreversible.',
                () => {
                  this.physicalAssetManagerService.removePhysicalAsset(asset.id);
                  this.physicalAssets.splice(this.physicalAssets.findIndex(x => x.id == asset.id), 1);
                  this.cdr.detectChanges();
                  this.assetTable.reloadData();
                });
            }
          }
        ] as TableAction[],
      };
    }) || [];
  }

  showPhysicalAsset(assetId: string) {
    this.router.navigate(['asset', assetId], { relativeTo: this.route });
  }

  async save() {

    switch (this.mode) {

      case CreationMode.NEW:
        const equipment = await this.equipmentManagerService.createEquipment(this.equipment);
        await this.router.navigate(['equipment/' + equipment.id], { replaceUrl: true });
        break;

      case CreationMode.UPDATE:
        await this.equipmentManagerService.updateEquipment(this.equipment.id, this.equipment);
        await this.router.navigate(['equipment']);
        break;
    }

  }

  async delete() {

    if (!this.canDelete()) {
      this.uiManagerService.showInfoDialog(this.translatePipe.transform('Cannot remove'), this.translatePipe.transform('You have to manually remove all the inner content before.'));
      return;
    }

    await this.uiManagerService.showConfirmDialog('Delete equipment', 'You will lose access to assets and resources linked to selected equipment.', async () => {
      await this.equipmentManagerService.removeEquipment(this.equipment.id);
      await this.router.navigate(['equipment']);
    });
  }

  canDelete() {
    return this.equipment.procedures.length == 0 && this.resources.length == 0 && this.physicalAssets.length == 0;
  }

  uploadImage() {
    const fileInput = document.createElement("input");
    fileInput.setAttribute("type", "file");
    fileInput.accept="image/png,image/gif,image/jpeg";
    fileInput.onchange = async e => {
      const file = e.target['files'][0];
      await this.equipmentManagerService.uploadImageEquipment(this.equipment.id, file);
      location.reload();
    }
    fileInput.click();
  }

  removeProcedure(procedureId: string) {
    this.uiManagerService.showConfirmDialog('Remove procedure', 'Are you sure do you want to remove this procedure?<br>This operation is irreversible.', () => {
      this.authoringManagerService.removeProcedure(procedureId);
      this.equipment.procedures.splice(this.equipment.procedures.findIndex(x => x.id == procedureId), 1);
    });
  }

  importAssets() {
    const fileInput = document.createElement("input");
    fileInput.setAttribute("type", "file");
    fileInput.accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    fileInput.onchange = async e => {
      const file = e.target['files'][0];
      this.isLoading = true;
      const response = await this.physicalAssetManagerService.importPhysicalAssets(this.equipment.id, file);
      if (response == ApiStatus.SUCCESS) {
        await this.paginatorPhysicalAssets.refresh();
      }
      this.isLoading = false;
    }
    fileInput.click();
  }

  downloadQRCodes() {
    this.physicalAssetManagerService.downloadQRCodes(this.equipment);
  }

  filterResources(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  filterProcedures(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  filterPhysicalAssets(filter: string) {
    this.physicalAssetManagerService.getPhysicalAssets(this.equipment.id, filter, this.paginatorPhysicalAssets);
  }

  newProcedure() {
    this.uiManagerService.showInputDialog('New procedure', 'Create', null, async (result: InputDialogData) => {

      // Create procedure object
      let procedure = new Procedure();
      procedure.modelId = this.equipment.id;
      procedure.name = result.input.name;
      procedure.description = result.input.description;

      // Create procedure
      procedure = await this.authoringManagerService.createProcedure(procedure);

      // Go to procedure
      await this.router.navigate(['authoring/' + procedure.id], { relativeTo: this.route });

    });
  }

  updateProcedure(procedure: Procedure) {
    this.uiManagerService.showInputDialog(
      'Update procedure',
      'Update',
      { input: { name: procedure.name, description: procedure.description } }, async (result: InputDialogData) => {

      // Update procedure object
      procedure.modelId = procedure.equipmentModel.id;
      procedure.name = result.input.name;
      procedure.description = result.input.description;

      // Update procedure
      procedure = await this.authoringManagerService.updateProcedure(procedure.id, procedure);

    });
  }

  getModes() {
    return CreationMode;
  }

  get ResourceType() {
    return ResourceType;
  }

  get ResourceUtils() {
    return ResourceUtils;
  }

  get DetailCardContent() { return DetailCardContent; }

  get environment() { return environment; }

}
